/**
 * Account Asymmetry — same setups, different results across accounts.
 *
 * For each setup the trader runs on multiple accounts, compute per-account
 * WR. When the gap between best- and worst-performing account on the SAME
 * setup is large, surface as a "what's different about THIS account
 * specifically" insight.
 *
 * Why this matters separately from accountPerformance:
 *   accountPerformance compares this account to the trader's overall
 *   baseline. This analyzer compares per-setup behavior across accounts —
 *   the question changes from "is this account dragging?" to "WHY is the
 *   same setup different on different accounts?". That's where
 *   prop-firm-rule effects, account-specific psychology, and
 *   size-anxiety patterns hide.
 *
 * Reads:
 *   trade.accountId, trade.setup, trade.netPnl
 *   ctx.allTrades — needed to compute per-setup × per-account distribution
 *   ctx.accountId, ctx.accountLabel — the focal account
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types.js';

export const key = 'accountAsymmetry';
export const tier = 'free';

const MIN_TRADES_PER_GROUP = 4;     // per-account-per-setup floor for the comparison
const MIN_WR_GAP           = 0.20;  // 20pp gap between account peers on the same setup
const MIN_PEER_ACCOUNTS    = 2;     // need at least 2 OTHER accounts running this setup

const fmtPct = (n) => `${Math.round(n * 100)}%`;
const isWin = (t) => Number.isFinite(t.netPnl) && t.netPnl > 0;
const wr = (group) => group.length === 0 ? 0 : group.filter(isWin).length / group.length;

/**
 * For a focal account, find setups it runs that ALSO run on other accounts,
 * and compare per-account WR. Returns observations for setups where the
 * focal account's WR diverges from peers by ≥ MIN_WR_GAP.
 */
const detectSetupAsymmetry = (focalAccountId, focalAccountLabel, allTrades) => {
  if (!Array.isArray(allTrades) || allTrades.length === 0) return [];

  // Bucket: setupName → accountId → trades[]
  const grid = new Map();
  for (const t of allTrades) {
    if (!t?.setup || !t?.accountId) continue;
    if (!grid.has(t.setup)) grid.set(t.setup, new Map());
    const acctMap = grid.get(t.setup);
    if (!acctMap.has(t.accountId)) acctMap.set(t.accountId, []);
    acctMap.get(t.accountId).push(t);
  }

  const out = [];
  for (const [setupName, acctMap] of grid) {
    const focalTrades = acctMap.get(focalAccountId) || [];
    if (focalTrades.length < MIN_TRADES_PER_GROUP) continue;
    // Peer accounts = OTHER accountIds running this setup with enough trades.
    const peers = [];
    for (const [aid, group] of acctMap) {
      if (aid === focalAccountId) continue;
      if (group.length >= MIN_TRADES_PER_GROUP) peers.push({ accountId: aid, trades: group });
    }
    if (peers.length < MIN_PEER_ACCOUNTS) continue;

    const focalWR = wr(focalTrades);
    // Pool peer trades to compute the peer-baseline WR (weighted by trade count).
    const peerPool = peers.flatMap(p => p.trades);
    const peerWR = wr(peerPool);
    const gap = focalWR - peerWR;
    if (Math.abs(gap) < MIN_WR_GAP) continue;

    const lagging = gap < 0;
    out.push(makeObservation({
      id: `${key}:${focalAccountId}:${setupName}:${lagging ? 'lag' : 'lead'}`,
      analyzer: key,
      scope: 'account',
      scopeId: focalAccountLabel,
      severity: lagging ? SEVERITY.WARNING : SEVERITY.SUCCESS,
      category: CATEGORY.SETUP,
      title: lagging
        ? `"${setupName}" wins ${fmtPct(focalWR)} on "${focalAccountLabel}" but ${fmtPct(peerWR)} on your other accounts`
        : `"${setupName}" wins ${fmtPct(focalWR)} on "${focalAccountLabel}" — ${fmtPct(gap)} better than your other accounts`,
      detail: lagging
        ? `${focalTrades.length} trades on this account vs ${peerPool.length} on ${peers.length} other accounts. Same setup, same trader; the variance is account-specific. Common causes: prop-firm rule constraint forces a worse exit, size-anxiety triggers different management, trading hours differ.`
        : `${focalTrades.length} trades on this account vs ${peerPool.length} on ${peers.length} other accounts. Whatever you're doing differently on this account works for this setup specifically — name it and replicate.`,
      action: lagging
        ? `Compare a recent ${setupName} loser on this account to a recent winner on a peer account. Look for management differences (BE earlier? Stopped earlier? Different ATM?) before blaming the setup.`
        : `Identify what's different about ${setupName} execution on this account vs others. Replicate the lever on the laggards.`,
      evidence: {
        accountId: focalAccountId, setup: setupName,
        focalWR, peerWR, gap,
        focalCount: focalTrades.length, peerCount: peerPool.length,
        peerAccountCount: peers.length,
      },
      confidence: 0.8,
      priority: lagging ? 80 : 74,
    }));
  }
  return out;
};

// ── Public analyze() ──────────────────────────────────────────────────────

export const analyze = (ctx) => {
  const { accountId, accountLabel, allTrades } = ctx || {};
  if (!accountId) return null;
  const observations = detectSetupAsymmetry(accountId, accountLabel || accountId, allTrades || []);
  return observations.length === 0 ? null : observations;
};

// Exports for tests
export {
  detectSetupAsymmetry,
  MIN_TRADES_PER_GROUP,
  MIN_WR_GAP,
  MIN_PEER_ACCOUNTS,
};
